import '../styles/main.scss';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { ErrorBoundary } from '@buzzfeed/react-components';
import { createPixiedustBroadcaster } from '@buzzfeed/pixiedust';
import { getDestination } from '@buzzfeed/bf-utils/lib/domain';
import { captureException } from '../sentry.client.config.js';
import { useDocumentReferrerPolyfill } from '../hooks/tracking/useDocumentReferrerPolyfill';
import '../public/static/images/bg-desktop.jpg';
import '../public/static/images/bg-mobile.jpg';
import thumb from '../public/static/images/island-thumb.jpg';
import { TrackingContext } from '../contexts';

import { CLUSTER, SERVICE_NAME } from '../constants';

const [sendEvent] = createPixiedustBroadcaster({ env: CLUSTER });

function Application({ Component, pageProps }) {
  useDocumentReferrerPolyfill();

  const onError = (error, errorInfo) => {
    const destination = getDestination(window.location.href);
    // send pixiedust event that will get turned into a datadog metric
    const event = {
      type: 'instrumentation',
      destination,
      service: SERVICE_NAME,
      platform: 'web',
      target: 'error',
      value: 'unhandled_page',
      source: 'web'
    };
    sendEvent(event);

    captureException(error, {
      level: 'fatal',
      extra: errorInfo,
      tags: {
        error_boundary: 'application',
        destination,
      }
    });
  };

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorPage statusCode={500} title="An unexpected error has occurred" />
      )}
      onError={(error, errorInfo) => {
        console.error(`Unexpected page error: ${error.message}`);
        onError(error, errorInfo);
      }}
    >
      <Head>
        <title>BuzzFeed Island</title>
        <meta name="title" content="BuzzFeed Island"></meta>
        <meta
          name="description"
          content="Sign up to keep up to date with BuzzFeed Island"
        ></meta>
        <meta
          name="copyright"
          content="Copyright BuzzFeed, Inc. All rights reserved."
        ></meta>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <link
          rel="icon"
          type="image/png"
          href="/static/images/island-mark.jpg"
        />
        <meta property="og:image" content={thumb}></meta>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        {/* TODO: remove instrument font when fully rebranded */}
        <link
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@500;600;700&family=Instrument+Serif:ital,wght@0,400;1,400&family=Pixelify+Sans:wght@400..700&display=swap"
          rel="stylesheet"
        />        
      </Head>
      <TrackingContext.Provider value={{ ...pageProps.tracking }}>
        <Component {...pageProps} />
      </TrackingContext.Provider>
    </ErrorBoundary>
  );
}

export default Application;
